import React from "react";
import moment from "moment";
import PropTypes from "prop-types";
import { Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Paper } from "@mui/material";

import Modal from "components/common/Modal";
import Button from "components/common/Button";

import ONE_TIME_INVOICE_ITEM_TYPE from "components/common/CreateOneTimeChargeModal/enums";

import "./CreateOneTimeChargeModal.scss";

function ConfirmChargeModal({ open, onClose, onConfirm, chargeData = [], isLoading = false }) {
  const formatChargeType = ({ type, employeeName, notes, startDate, endDate }) => {
    let message = "";
    if (type === ONE_TIME_INVOICE_ITEM_TYPE.Upsell && employeeName) {
      const currentDate = new Date();
      const monthYear = currentDate.toLocaleDateString("en-US", {
        month: "short",
        year: "numeric",
      });
      message = `Fee for ${monthYear} - ${employeeName}`;
    }

    if (type === ONE_TIME_INVOICE_ITEM_TYPE.Equipment) {
      message = "Equipment";
    }
    if (type === ONE_TIME_INVOICE_ITEM_TYPE.Miscellaneous) {
      message = "Miscellaneous";
    }
    if (type === ONE_TIME_INVOICE_ITEM_TYPE.HolidayCompensation && employeeName && startDate) {
      const format = "MMM D, YYYY";
      message = `Holiday compensation - ${employeeName} - ${moment(startDate).format(format)}`;
      if (endDate && startDate !== endDate) {
        message += ` - ${moment(endDate).format(format)}`;
      }
    }
    if (type === ONE_TIME_INVOICE_ITEM_TYPE.LateFee) {
      message = "Late fee";
    }

    if (notes) {
      message += ` - ${notes}`;
    }

    return message;
  };

  const formatCurrency = (amount) => {
    const safeAmount = amount !== null && amount !== undefined ? Number(amount) : 0;
    return `$${safeAmount.toFixed(2)}`;
  };

  const calculateTotal = () => {
    if (!Array.isArray(chargeData) || chargeData.length === 0) return 0;

    return chargeData.reduce((total, charge) => {
      if (!charge) return total;

      const { type = "", fee = 0, implementationFee = 0, discount = 0 } = charge;
      let chargeTotal = 0;

      if (type === ONE_TIME_INVOICE_ITEM_TYPE.Upsell) {
        const monthlyFee = Number(fee) || 0;
        const implementationFeeValue = Number(implementationFee) || 0;
        chargeTotal = monthlyFee + implementationFeeValue;
      } else {
        const feeValue = Number(fee) || 0;
        chargeTotal = feeValue;
      }

      const discountValue = Number(discount) || 0;
      return total + chargeTotal - discountValue;
    }, 0);
  };

  const calculateSubtotal = () => {
    if (!Array.isArray(chargeData) || chargeData.length === 0) return 0;

    return chargeData.reduce((total, charge) => {
      if (!charge) return total;

      const { type = "", fee = 0, implementationFee = 0 } = charge;

      if (type === ONE_TIME_INVOICE_ITEM_TYPE.Upsell) {
        const monthlyFee = Number(fee) || 0;
        const implementationFeeValue = Number(implementationFee) || 0;
        return total + monthlyFee + implementationFeeValue;
      }
      return total + (Number(fee) || 0);
    }, 0);
  };

  const calculateTotalDiscount = () => {
    if (!Array.isArray(chargeData) || chargeData.length === 0) return 0;

    return chargeData.reduce((acc, charge) => {
      if (!charge) return acc;

      const { discount = 0 } = charge;
      return acc + (Number(discount) || 0);
    }, 0);
  };

  const tableData = Array.isArray(chargeData)
    ? chargeData.map((charge, index) => {
        if (!charge) {
          return {
            id: index,
            chargeType: "Unknown",
            implementationFee: "",
            fee: formatCurrency(0),
            discount: "",
            total: formatCurrency(0),
          };
        }

        const { type = "", fee = 0, implementationFee = 0, discount = 0 } = charge;

        const chargeTotal =
          type === ONE_TIME_INVOICE_ITEM_TYPE.Upsell
            ? (Number(fee) || 0) + (Number(implementationFee) || 0) - (Number(discount) || 0)
            : (Number(fee) || 0) - (Number(discount) || 0);

        return {
          id: index,
          chargeType: formatChargeType(charge),
          implementationFee: type === ONE_TIME_INVOICE_ITEM_TYPE.Upsell ? formatCurrency(implementationFee) : "",
          fee: formatCurrency(fee),
          discount: discount ? `(${formatCurrency(discount)})` : "",
          total: formatCurrency(chargeTotal),
        };
      })
    : [];

  const subtotal = calculateSubtotal();
  const totalDiscount = calculateTotalDiscount();
  const total = calculateTotal();

  const subtotalImplementationFee = Array.isArray(chargeData)
    ? chargeData.reduce((acc, charge) => {
        if (!charge) return acc;

        const { type = "", implementationFee = 0 } = charge;
        return acc + (type === ONE_TIME_INVOICE_ITEM_TYPE.Upsell ? Number(implementationFee) || 0 : 0);
      }, 0)
    : 0;

  const subtotalFee = Array.isArray(chargeData)
    ? chargeData.reduce((acc, charge) => {
        if (!charge) return acc;

        const { fee = 0 } = charge;
        return acc + (Number(fee) || 0);
      }, 0)
    : 0;

  return (
    <Modal open={open} onClose={onClose} title="Confirm and Create Charge" width="800px">
      <div className="confirm-charge-modal-content">
        <p className="confirm-charge-description">
          You're about to create a one-time charge that will be billed to the customer immediately. This action cannot
          be edited or undone once submitted.
        </p>

        <div className="charge-table-container">
          <TableContainer component={Paper} elevation={0} className="custom-table-container">
            <Table className="custom-table" style={{ tableLayout: "fixed" }}>
              <colgroup>
                <col style={{ width: "40%" }} />
                <col style={{ width: "15%" }} />
                <col style={{ width: "15%" }} />
                <col style={{ width: "15%" }} />
                <col style={{ width: "15%" }} />
              </colgroup>
              <TableHead>
                <TableRow>
                  <TableCell className="table-header-cell pl-1">Charge type</TableCell>
                  <TableCell className="table-header-cell implementation-fee-header" align="right">
                    Implementation&nbsp;fee
                  </TableCell>
                  <TableCell className="table-header-cell fee-header" align="right">
                    Fee
                  </TableCell>
                  <TableCell className="table-header-cell discount-header" align="right">
                    Discount
                  </TableCell>
                  <TableCell className="table-header-cell pr-1" align="right">
                    Total
                  </TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {tableData.map((row) => (
                  <TableRow key={row.id} className="table-body-row">
                    <TableCell className="table-body-cell charge-type-cell">{row.chargeType}</TableCell>
                    <TableCell className="table-body-cell implementation-fee-cell" align="right">
                      {row.implementationFee}
                    </TableCell>
                    <TableCell className="table-body-cell fee-cell" align="right">
                      {row.fee}
                    </TableCell>
                    <TableCell className="table-body-cell discount-cell" align="right">
                      {row.discount}
                    </TableCell>
                    <TableCell className="table-body-cell total-cell" align="right">
                      {row.total}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>

          <div className="subtotal-section">
            <div className="subtotal-row">
              <div className="subtotal-cell charge-type">Subtotal</div>
              <div className="subtotal-cell implementation-fee">{formatCurrency(subtotalImplementationFee)}</div>
              <div className="subtotal-cell fee">{formatCurrency(subtotalFee)}</div>
              <div className="subtotal-cell discount">
                {totalDiscount > 0 ? `(${formatCurrency(totalDiscount)})` : ""}
              </div>
              <div className="subtotal-cell total">{formatCurrency(subtotal)}</div>
            </div>

            <div className="total-amount-row">
              <div className="total-label">Total amount</div>
              <div className="total-value">{formatCurrency(total)}</div>
            </div>
          </div>
        </div>

        <div className="flex justify-end gap-1 mt-1">
          <Button variant="text" size="large" onClick={onClose} label="Go Back" />
          <Button variant="contained" size="large" onClick={onConfirm} isLoading={isLoading} label="Confirm & Charge" />
        </div>
      </div>
    </Modal>
  );
}

ConfirmChargeModal.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  onConfirm: PropTypes.func.isRequired,
  chargeData: PropTypes.arrayOf(
    PropTypes.shape({
      type: PropTypes.string,
      fee: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
      implementationFee: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
      discount: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
      employeeName: PropTypes.string,
    })
  ).isRequired,
  isLoading: PropTypes.bool,
};

export default ConfirmChargeModal;
